import { ChevronLeft, ChevronRight, ChevronsLeft } from 'lucide-react';

import { useTranslation } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';

import { DEFAULT_LIMIT, LIMIT_OPTIONS } from './cursor-search';
import type { CursorPager } from './use-cursor-pager';

export interface CursorPaginationProps {
  pager: CursorPager;
  /** `nextCursor` from the list response; null on the last page, undefined while loading. */
  nextCursor: string | null | undefined;
  limit?: number | undefined;
  onLimitChange?: ((limit: number) => void) | undefined;
  isFetching?: boolean;
  /** Rows on the current page, for "Showing N". */
  count?: number | undefined;
  /** Replaces "Showing N": "20 of 63 products". */
  summary?: string | undefined;
  selectedCount?: number | undefined;
}

/** Next / previous / first controls for cursor-paginated lists. There is a next page iff `nextCursor` is a string. */
export function CursorPagination({
  pager,
  nextCursor,
  limit = DEFAULT_LIMIT,
  onLimitChange,
  isFetching = false,
  count,
  summary,
  selectedCount,
}: CursorPaginationProps) {
  const { t } = useTranslation('common');
  const hasMore = typeof nextCursor === 'string';
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-1 py-2 text-sm">
      <div className="text-muted-foreground">
        {selectedCount ? t('table.selected', { count: selectedCount }) : null}
        {selectedCount && count !== undefined ? ' · ' : null}
        {summary ?? (count !== undefined ? t('table.showing', { count }) : null)}
      </div>
      <div className="flex items-center gap-2">
        {onLimitChange ? (
          <Select value={String(limit)} onValueChange={(v) => onLimitChange(Number(v))}>
            <SelectTrigger className="h-8 w-[110px]" aria-label={t('table.rowsPerPage')}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LIMIT_OPTIONS.map((n) => (
                <SelectItem key={n} value={String(n)}>
                  {t('table.perPage', { count: n })}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : null}
        <Button
          variant="outline"
          size="icon-sm"
          onClick={pager.goFirst}
          disabled={!pager.canPrev && !hasMore && count === undefined}
          aria-label={t('table.firstPage')}
        >
          <ChevronsLeft aria-hidden />
        </Button>
        <Button
          variant="outline"
          size="icon-sm"
          onClick={pager.goPrev}
          disabled={!pager.canPrev || isFetching}
          aria-label={t('table.previousPage')}
        >
          <ChevronLeft aria-hidden />
        </Button>
        <Button
          variant="outline"
          size="icon-sm"
          onClick={() => hasMore && pager.goNext(nextCursor)}
          disabled={!hasMore || isFetching}
          aria-label={t('table.nextPage')}
        >
          <ChevronRight aria-hidden />
        </Button>
      </div>
    </div>
  );
}
